import { Trans, useLingui } from "@lingui/react/macro";
import styles from "./Activities.module.scss";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { usePatientsActivityOccurrencesAndGroups } from "@/api/Activities";
import NoResults from "@/components/NoResults/NoResults";
import { useParentRoutesPatient } from "@/api/Patients";
import { useUserProfile } from "@/api/Users";
import {
  addDays,
  subDays,
  differenceInCalendarDays,
  startOfWeek,
  endOfWeek,
  isSameDay,
} from "date-fns";
import { dateName } from "@/Utils/DateUtils";
import { Outlet, useNavigate } from "react-router";
import { PlainButton } from "@medoma/components/Button/Button";
import { SplitPane } from "@/components/SplitPane/SplitPane";
import { deducedError } from "@/Utils/ErrorUtils";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import ArrowUpIcon from "@medoma/components/icons/ArrowUpIcon";
import ArrowDownIcon from "@medoma/components/icons/ArrowDownIcon";
import { useState } from "react";
import * as Sentry from "@sentry/react";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import BulletListIcon from "@medoma/components/icons/BulletListIcon";
import { Text } from "@medoma/components/Text/Text";
import CalendarIcon from "@medoma/components/icons/CalendarIcon";
import { z } from "zod";
import { ActivitiesCalendar } from "./ActivitiesCalendar/ActivitiesCalendar";
import { useLocalStorage } from "@medoma/shared-employee/useLocalStorage";
import { ActivitiesList } from "./ActivitiesList/ActivitiesList";
import { getPatientFullName } from "@medoma/models/patients";
import { SelectDatePopover } from "@/components/SelectDatePopover/SelectDatePopover";
import ClipboardListIcon from "@medoma/components/icons/ClipboardListIcon";
import BasePlan from "./BasePlan/BasePlan";

// Enforced by backend
const MAXIMUM_ALLOWED_SPAN_DAYS = 100;
// The number 15 for list has its roots here: https://linear.app/medoma/issue/MED-4661/see-more-days-on-patient
export const DAYS_AHEAD_TO_SHOW_ACTIVITIES_IN_LIST = 14;
// IMPORTANT: May not be larger than (MAXIMUM_ALLOWED_SPAN_DAYS - 12 days) = 88 days
// due to `startOfWeek` and `endOfWeek` functions in calendarViewModeConfig below.
export const DAYS_AHEAD_TO_SHOW_ACTIVITIES_IN_CALENDAR = 28;

const viewModes = ["list", "calendar", "basePlan"] as const;
const viewModesSchema = z.enum(viewModes);
type IViewMode = z.infer<typeof viewModesSchema>;

const isSwitchingToOrFromBasePlan = ({
  currentViewMode,
  newViewMode,
}: {
  currentViewMode: IViewMode;
  newViewMode: IViewMode;
}) => {
  return (
    (currentViewMode === viewModesSchema.enum.basePlan &&
      newViewMode !== viewModesSchema.enum.basePlan) ||
    (currentViewMode !== viewModesSchema.enum.basePlan &&
      newViewMode === viewModesSchema.enum.basePlan)
  );
};

const ViewSelectButton = ({ viewMode }: { viewMode: IViewMode }) => {
  if (viewMode === viewModesSchema.enum.list) {
    return (
      <div className={styles.viewModeSelectorButton}>
        <BulletListIcon />
        <Text element="div">
          <Trans>Lista</Trans>
        </Text>
      </div>
    );
  }

  if (viewMode === viewModesSchema.enum.calendar) {
    return (
      <div className={styles.viewModeSelectorButton}>
        <CalendarIcon />
        <Text element="div">
          <Trans>Kalender</Trans>
        </Text>
      </div>
    );
  }

  if (viewMode === viewModesSchema.enum.basePlan) {
    return (
      <div className={styles.viewModeSelectorButton}>
        <ClipboardListIcon />
        <Text element="div">
          <Trans>Grundplanering</Trans>
        </Text>
      </div>
    );
  }
};

const Activities = () => {
  const { t } = useLingui();
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  const { userId } = useUserProfile();
  const [selectedViewMode, setSelectedViewMode] = useLocalStorage({
    key: "patient.activitiesViewMode",
    userId,
    initialValue: viewModesSchema.enum.list,
  });

  const today = new Date();

  const listViewModeConfig = {
    initialFromDate: today,
    initialToDate: addDays(today, DAYS_AHEAD_TO_SHOW_ACTIVITIES_IN_LIST),
    expandByDays: 1,
  };

  const calendarViewModeConfig = {
    initialFromDate: startOfWeek(today, { weekStartsOn: 1 }),
    initialToDate: endOfWeek(
      addDays(today, DAYS_AHEAD_TO_SHOW_ACTIVITIES_IN_CALENDAR),
      { weekStartsOn: 1 },
    ),
    expandByDays: 7,
  };

  const [from, setFrom] = useState<Date>(
    selectedViewMode === viewModesSchema.enum.list
      ? listViewModeConfig.initialFromDate
      : calendarViewModeConfig.initialFromDate,
  );
  const [to, setTo] = useState<Date>(
    selectedViewMode === viewModesSchema.enum.list
      ? listViewModeConfig.initialToDate
      : calendarViewModeConfig.initialToDate,
  );

  const handleViewModeChange = (viewMode: IViewMode) => {
    // If switching to or from base plan, we close the details panel to avoid inconsistencies in highlighting items in list/calendar
    if (
      isSwitchingToOrFromBasePlan({
        currentViewMode: viewModesSchema.parse(selectedViewMode),
        newViewMode: viewMode,
      })
    ) {
      void navigate(`/commandcenter/patients/${patient.id}/activities`);
    }

    if (viewMode === viewModesSchema.enum.list) {
      setFrom(listViewModeConfig.initialFromDate);
      setTo(listViewModeConfig.initialToDate);
    } else {
      setFrom(calendarViewModeConfig.initialFromDate);
      setTo(calendarViewModeConfig.initialToDate);
    }

    setSelectedViewMode(viewMode);
  };

  const dateSpanInDays = differenceInCalendarDays(to, from) + 1; // +1 to include the end date

  const {
    data: activityOccurrencesAndGroups,
    isPending,
    isError,
    error,
    isPlaceholderData,
  } = usePatientsActivityOccurrencesAndGroups(patient.id, from, to);

  if (isPending) {
    return (
      <Loading
        message={t`Laddar patientens aktivitetstillfällen`}
        padding={24}
      />
    );
  }

  if (isError) {
    Sentry.captureException(error);
    return <ErrorMessage message={deducedError(error)} padding={24} />;
  }

  const expandDaysBack = (numberOfDays: number) => {
    // We want to make sure we don't create a larger date span than the maximum allowed
    if (dateSpanInDays + numberOfDays > MAXIMUM_ALLOWED_SPAN_DAYS) {
      setTo((prev) => subDays(prev, numberOfDays));
    }
    setFrom((prev) => subDays(prev, numberOfDays));
  };

  const expandDaysAhead = (numberOfDays: number) => {
    // We want to make sure we don't create a larger date span than the maximum allowed
    if (dateSpanInDays + numberOfDays > MAXIMUM_ALLOWED_SPAN_DAYS) {
      setFrom((prev) => addDays(prev, numberOfDays));
    }
    setTo((prev) => addDays(prev, numberOfDays));
  };

  const displayedDays = Array.from({ length: dateSpanInDays }, (_, index) =>
    addDays(from, index),
  );

  const patientName = getPatientFullName(patient.name);
  const startDateName = dateName(from);
  const endDateName = dateName(to);
  const hasActivityOccurrencesOrGroups =
    activityOccurrencesAndGroups.length > 0;

  const renderViewModeSelector = () => (
    <div className={styles.viewModeSelectorGroup}>
      <RadioButton
        label={{
          text: t`Lista`,
          component: <ViewSelectButton viewMode={viewModesSchema.enum.list} />,
        }}
        name="view—selector"
        value={viewModesSchema.enum.list}
        checked={selectedViewMode === viewModesSchema.enum.list}
        onChange={() => handleViewModeChange(viewModesSchema.enum.list)}
        visualStyle="framed"
      />
      <RadioButton
        label={{
          text: t`Kalender`,
          component: (
            <ViewSelectButton viewMode={viewModesSchema.enum.calendar} />
          ),
        }}
        name="view—selector"
        value={viewModesSchema.enum.calendar}
        checked={selectedViewMode === viewModesSchema.enum.calendar}
        onChange={() => handleViewModeChange(viewModesSchema.enum.calendar)}
        visualStyle="framed"
      />
      <RadioButton
        label={{
          text: t`Grundplanering`,
          component: (
            <ViewSelectButton viewMode={viewModesSchema.enum.basePlan} />
          ),
        }}
        name="view—selector"
        value={viewModesSchema.enum.basePlan}
        checked={selectedViewMode === viewModesSchema.enum.basePlan}
        onChange={() => handleViewModeChange(viewModesSchema.enum.basePlan)}
        visualStyle="framed"
      />
    </div>
  );

  const renderShowEarlierButton = () => {
    if (selectedViewMode === viewModesSchema.enum.calendar) {
      return (
        <PlainButton
          onClick={() => expandDaysBack(calendarViewModeConfig.expandByDays)}
          disabled={isPlaceholderData}
        >
          <ArrowUpIcon />
          <Trans>Visa föregående vecka</Trans>
        </PlainButton>
      );
    }

    return (
      <PlainButton
        onClick={() => expandDaysBack(listViewModeConfig.expandByDays)}
        disabled={isPlaceholderData}
      >
        <ArrowUpIcon />
        <Trans>Visa föregående dag</Trans>
      </PlainButton>
    );
  };

  const renderShowLaterButton = () => {
    if (selectedViewMode === viewModesSchema.enum.calendar) {
      return (
        <PlainButton
          onClick={() => expandDaysAhead(calendarViewModeConfig.expandByDays)}
          disabled={isPlaceholderData}
        >
          <ArrowDownIcon />
          <Trans>Visa nästa vecka</Trans>
        </PlainButton>
      );
    }

    return null;
  };

  const handleDateSelect = (date: Date) => {
    if (selectedViewMode === viewModesSchema.enum.list) {
      setFrom(date);
      setTo(addDays(date, DAYS_AHEAD_TO_SHOW_ACTIVITIES_IN_LIST));
    } else {
      setFrom(startOfWeek(date, { weekStartsOn: 1 }));
      setTo(
        endOfWeek(addDays(date, DAYS_AHEAD_TO_SHOW_ACTIVITIES_IN_CALENDAR), {
          weekStartsOn: 1,
        }),
      );
    }
  };

  const renderGoToTodayButton = () => {
    const showButton =
      selectedViewMode === viewModesSchema.enum.list
        ? !isSameDay(from, today)
        : !isSameDay(from, startOfWeek(today, { weekStartsOn: 1 }));

    const goToToday = () => {
      if (selectedViewMode === viewModesSchema.enum.list) {
        setFrom(listViewModeConfig.initialFromDate);
        setTo(listViewModeConfig.initialToDate);
      } else {
        setFrom(calendarViewModeConfig.initialFromDate);
        setTo(calendarViewModeConfig.initialToDate);
      }
    };

    if (showButton) {
      return (
        <PlainButton onClick={goToToday} disabled={isPlaceholderData}>
          <Trans>Gå till idag</Trans>
        </PlainButton>
      );
    }

    return null;
  };

  const renderToolbarTop = () => (
    <div className={styles.activitiesControls}>
      {renderViewModeSelector()}
      <PlainButton onClick={() => navigate("new")}>
        <PlusIcon />
        <Trans>Skapa aktivitet</Trans>
      </PlainButton>
    </div>
  );

  const renderDateNavigationTop = () => (
    <div className={styles.activitiesControls}>
      {renderShowEarlierButton()}
      <SelectDatePopover
        selectedDate={from}
        onDateSelect={handleDateSelect}
        disabled={isPlaceholderData}
      />
    </div>
  );

  const renderDateNavigationBottom = () => (
    <div className={styles.activitiesControls}>
      {renderShowLaterButton()}
      <SelectDatePopover
        selectedDate={from}
        onDateSelect={handleDateSelect}
        disabled={isPlaceholderData}
      />
    </div>
  );

  const renderViewModeContent = () => {
    if (selectedViewMode === viewModesSchema.enum.basePlan) {
      return <BasePlan />;
    }

    if (selectedViewMode === viewModesSchema.enum.list) {
      return (
        <>
          {renderDateNavigationTop()}
          {renderGoToTodayButton()}
          {hasActivityOccurrencesOrGroups ? (
            <ActivitiesList
              activityOccurrencesAndGroups={activityOccurrencesAndGroups}
              displayedDays={displayedDays}
              isPlaceholderData={isPlaceholderData}
              patientName={patientName}
            />
          ) : (
            <NoResults
              message={t`${patientName} har inga aktiviteter mellan ${startDateName} och ${endDateName}`}
            />
          )}
          {renderDateNavigationBottom()}
        </>
      );
    }

    if (selectedViewMode === viewModesSchema.enum.calendar) {
      return (
        <>
          {renderDateNavigationTop()}
          {renderGoToTodayButton()}
          {hasActivityOccurrencesOrGroups ? (
            <ActivitiesCalendar
              activityOccurrencesAndGroups={activityOccurrencesAndGroups}
              displayedDays={displayedDays}
              isPlaceholderData={isPlaceholderData}
            />
          ) : (
            <NoResults
              message={t`${patientName} har inga aktiviteter mellan ${startDateName} och ${endDateName}`}
            />
          )}
          {renderDateNavigationBottom()}
        </>
      );
    }

    return (
      <>
        <NoResults
          message={t`Något gick fel. Ladda om sidan. Om felet kvarstår, kontakta support.`}
        />
      </>
    );
  };

  return (
    <SplitPane panelSizeLocalStorageKey="patient-activities">
      <div className={styles.activities}>
        {renderToolbarTop()}
        {renderViewModeContent()}
      </div>
      <Outlet />
    </SplitPane>
  );
};

export default Activities;
